import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { Box, Tabs, Tab, Typography } from '@mui/material';

import ContainerLoadingIndicator from 'src/components/common/loading/ContainerLoadingIndicator';
import PageWithLayout from 'src/components/layout/PageWithLayout';
import QuestionnaireForm from 'src/features/questionnaire/components/QuestionnaireForm';
import { ITabPanelProps } from 'src/features/questionnaire/domain/types';
import { QUESTIONNAIRE_LOADING_KEY } from 'src/features/questionnaire/store';
import { IQuestionnaireTemplate } from 'src/features/questionnaire_template/domain/types';
import {
	getQuestionnaireTemplates,
	QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
} from 'src/features/questionnaire_template/store/questionnaireTemplateThunks';
import { getStudyDetails } from 'src/features/study';
import { RootState, AppDispatch } from 'src/store';

export function CustomTabPanel(props: ITabPanelProps) {
	const { children, value, index, manualLoading, ...other } = props;

	return (
		<div
			role='tabpanel'
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
			style={{ position: 'relative' }}
		>
			{value === index && (
				<Box sx={{ px: { xm: 0, sm: 3 }, py: 3 }}>
					<ContainerLoadingIndicator
						loadingKey={QUESTIONNAIRE_LOADING_KEY}
						manualLoading={manualLoading}
						loaderType='linear'
						showAnimatedIcon={true}
					/>
					{children}
				</Box>
			)}
		</div>
	);
}

export function a11yProps(index: string) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	};
}

const QuestionnairePage: React.FC = () => {
	const dispatch = useDispatch<AppDispatch>();
	const { studyId, siteSlug } = useParams<{
		studyId: string;
		siteSlug: string;
	}>();
	const [searchParams, setSearchParams] = useSearchParams();

	const isTemplateLoading = useSelector(
		(state: { loading: ILoadingState }) => {
			return state.loading.loadingStates[
				QUESTIONNAIRE_TEMPLATE_LOADING_KEY
			];
		},
	);
	const isQuestionnaireLoading = useSelector(
		(state: { loading: ILoadingState }) => {
			return state.loading.loadingStates[QUESTIONNAIRE_LOADING_KEY];
		},
	);
	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);
	const studyDetails = useSelector(
		(state: RootState) => state.study.studyDetails,
	);
	const patientDetails = useSelector(
		(state: RootState) => state.patient.patientDetails,
	);

	const [activeQuestionnaireId, setActiveQuestionnaireId] = useState<
		string | null
	>();
	const [questionnaireTemplates, setQuestionnaireTemplates] = useState<
		IQuestionnaireTemplate[] | null
	>(null);

	const IsQuestionnaireDisabled =
		!siteDetails?.roles.includes('data_collector');

	const breadcrumbs = [
		{
			label: siteDetails?.name || 'Site Details',
			href: `/site/${siteSlug}`,
		},
		{
			label: studyDetails?.patient?.site_patient_id || 'Patient ID',
			href: `/site/${siteSlug}/patient/${studyDetails?.patient?.id}`,
		},
		{
			label: studyDetails?.patient_study_id || 'Study ID',
			href: `/site/${siteSlug}/patient/${studyDetails?.patient?.id}/study/${studyDetails?.id}`,
		},
	];

	const handleChange = (
		event: React.SyntheticEvent | undefined,
		newValue: string,
	) => {
		setActiveQuestionnaireId(newValue);

		const newParams = new URLSearchParams(searchParams);
		newParams.set('questionnaire_id', newValue);
		setSearchParams(newParams);
	};

	useEffect(() => {
		if (!studyId || !siteSlug || !patientDetails) {
			return;
		}
		dispatch(
			getStudyDetails({
				studyId: Number(studyId),
				siteSlug: siteSlug,
			}),
		);

		dispatch(
			getQuestionnaireTemplates({
				siteSlug: siteSlug,
				requestParams: {
					sort: 'index',
					order: 'asc',
					only_published: true,
				},
				onSuccess: (response) => {
					if (response.length > 0) {
						setQuestionnaireTemplates(response);

						const questionnaireID =
							searchParams.get('questionnaire_id');

						const isValidId = response.some(
							(template) =>
								String(template.id) === questionnaireID,
						);

						if (isValidId) {
							setActiveQuestionnaireId(questionnaireID);
						} else {
							handleChange(undefined, String(response[0].id));
						}
					}
				},
			}),
		);
	}, [studyId, siteSlug]);

	return (
		<PageWithLayout breadcrumbs={breadcrumbs}>
			<Box
				sx={{
					p: 3,
					marginTop: '20px',
					position: 'relative',
					bgcolor: 'background.paper',
					borderRadius: 2,
					width: '100%',
				}}
			>
				<Typography
					sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: { xs: '16px', sm: '20px' },
						lineHeight: '100%',
						letterSpacing: '0.15px',
						mb: 2,
						textAlign: 'left',
					}}
				>
					{`Questionnaires (${studyDetails?.study_name})`}
				</Typography>

				{isTemplateLoading || !questionnaireTemplates ? (
					<Box sx={{ p: 3 }}>
						<ContainerLoadingIndicator
							loadingKey={QUESTIONNAIRE_TEMPLATE_LOADING_KEY}
						/>
						No questionnaire template available.
					</Box>
				) : (
					activeQuestionnaireId && (
						<>
							<Box
								sx={{
									borderBottom: 1,
									borderColor: 'divider',
									maxWidth: { xs: '100vw', sm: '100%' },
									width: '100%',
								}}
							>
								<Tabs
									value={activeQuestionnaireId}
									onChange={handleChange}
									aria-label='questionnaire tabs'
									variant='scrollable'
									scrollButtons='auto'
									allowScrollButtonsMobile
									sx={{
										'& .MuiTabs-scrollButtons': {
											'&.Mui-disabled': { opacity: 0.3 },
										},
									}}
								>
									{questionnaireTemplates.map((template) => (
										<Tab
											key={template.id}
											label={template.name}
											value={String(template.id)}
											disabled={
												isQuestionnaireLoading &&
												searchParams.get(
													'questionnaire_id',
												) !== String(template.id)
											}
											{...a11yProps(
												`${template.name}-${template.id}`,
											)}
										/>
									))}
								</Tabs>
							</Box>
							{questionnaireTemplates.map((templates) => (
								<CustomTabPanel
									key={templates.id}
									value={activeQuestionnaireId}
									index={String(templates.id)}
								>
									{activeQuestionnaireId && (
										<QuestionnaireForm
											questionnaireId={Number(
												activeQuestionnaireId,
											)}
											isFormDisabled={
												IsQuestionnaireDisabled
											}
										/>
									)}
								</CustomTabPanel>
							))}
						</>
					)
				)}
			</Box>
		</PageWithLayout>
	);
};

export default QuestionnairePage;
